import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Paperclip, SendHorizontal, X } from "lucide-react";
import type { Attachment, UploadAttachmentOptions } from "../../backend/api/chat";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { cn } from "../../lib/utils";
import {
  ACCEPT_ATTRIBUTE,
  attachmentSizeLabel,
  describeFile,
  isAcceptedFile,
} from "./attachmentRules";

/** Mirrors MAX_BODY_CHARS in api/src/db/chat.rs. */
export const MAX_MESSAGE_CHARS = 4000;

/** Only warn once the limit is actually in sight. */
const COUNTER_VISIBLE_FROM = MAX_MESSAGE_CHARS - 200;

/** Matches the server's typing timeout with room to spare. */
const TYPING_IDLE_MS = 3_000;

/** Mirrors MAX_ATTACHMENTS_PER_MESSAGE in api/src/db/chat_attachments.rs. */
export const MAX_ATTACHMENTS = 4;

type PendingAttachment = {
  /** Local identity, since the server id only exists once the upload lands. */
  key: string;
  file: File;
  status: "uploading" | "ready" | "failed";
  /** `[0, 1]`, from the upload's progress events. */
  progress: number;
  attachment?: Attachment;
  error?: string;
};

type MessageComposerProps = {
  onSend: (body: string, attachmentIds?: string[]) => Promise<void>;
  onTyping?: (typing: boolean) => void;
  /** Absent on a surface that cannot upload, which hides the attach control. */
  onUpload?: (
    file: File,
    options: UploadAttachmentOptions,
  ) => Promise<Attachment>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
};

let pendingKeySeed = 0;

export function MessageComposer({
  onSend,
  onTyping,
  onUpload,
  disabled = false,
  placeholder = "Write a message",
  className,
}: MessageComposerProps) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A refused file and a failed send both surface here, but only a failed send
  // is worth retrying: retrying a file the API will not accept just refuses it
  // again.
  const [errorKind, setErrorKind] = useState<"send" | "attachment">("send");
  const [statusMessage, setStatusMessage] = useState("");
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const typingRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const stopTyping = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (typingRef.current) {
      typingRef.current = false;
      onTyping?.(false);
    }
  }, [onTyping]);

  useEffect(() => stopTyping, [stopTyping]);

  const handleChange = (value: string) => {
    setBody(value);
    if (!onTyping) return;

    if (value.trim() && !typingRef.current) {
      typingRef.current = true;
      onTyping(true);
    }
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(stopTyping, TYPING_IDLE_MS);
  };

  const update = useCallback(
    (key: string, changes: Partial<PendingAttachment>) => {
      setPending((current) =>
        current.map((item) => (item.key === key ? { ...item, ...changes } : item)),
      );
    },
    [],
  );

  const startUpload = useCallback(
    async (entry: PendingAttachment) => {
      if (!onUpload) return;
      update(entry.key, { status: "uploading", progress: 0, error: undefined });
      setStatusMessage(`Uploading ${entry.file.name}`);
      try {
        const attachment = await onUpload(entry.file, {
          onProgress: (fraction) => update(entry.key, { progress: fraction }),
        });
        update(entry.key, { status: "ready", progress: 1, attachment });
        setStatusMessage(`${entry.file.name} uploaded`);
      } catch (uploadError) {
        // The file stays in the composer with a retry. Never silently dropped.
        const message =
          uploadError instanceof Error
            ? uploadError.message
            : "That file could not be uploaded.";
        update(entry.key, { status: "failed", error: message });
        setStatusMessage(`${entry.file.name} could not be uploaded. ${message}`);
      }
    },
    [onUpload, update],
  );

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files || !onUpload) return;
      setError(null);

      const room = MAX_ATTACHMENTS - pending.length;
      if (room <= 0) {
        setErrorKind("attachment");
        setError(`A message carries at most ${MAX_ATTACHMENTS} attachments.`);
        return;
      }

      const accepted: PendingAttachment[] = [];
      for (const file of Array.from(files).slice(0, room)) {
        // Checked here so an obviously wrong file is refused before the bytes
        // leave the browser. The API checks again, and that is the check that
        // counts.
        const refusal = isAcceptedFile(file);
        if (refusal) {
          setErrorKind("attachment");
          setError(refusal);
          continue;
        }
        pendingKeySeed += 1;
        accepted.push({
          key: `pending-${pendingKeySeed}`,
          file,
          status: "uploading",
          progress: 0,
        });
      }

      if (accepted.length === 0) return;
      if (files.length > room) {
        setErrorKind("attachment");
        setError(`Only ${room} more attachment${room === 1 ? "" : "s"} fit on this message.`);
      }

      setPending((current) => [...current, ...accepted]);
      accepted.forEach((entry) => void startUpload(entry));
    },
    [onUpload, pending.length, startUpload],
  );

  const removePending = (key: string) => {
    setPending((current) => current.filter((item) => item.key !== key));
  };

  const uploading = pending.some((item) => item.status === "uploading");
  // A failed file has to be retried or removed first, or sending would clear
  // it from the composer without it ever reaching the conversation.
  const uploadFailed = pending.some((item) => item.status === "failed");
  const readyIds = pending
    .filter((item) => item.status === "ready" && item.attachment)
    .map((item) => (item.attachment as Attachment).id);

  const submit = async () => {
    const trimmed = body.trim();
    if (sending || disabled || uploading || uploadFailed) return;
    // A photo with no caption is an ordinary message, so either half is enough.
    if (!trimmed && readyIds.length === 0) return;

    setSending(true);
    setError(null);
    try {
      // Called with one argument when there is nothing attached, so a plain
      // text send stays a plain text send for every existing caller.
      await (readyIds.length > 0 ? onSend(trimmed, readyIds) : onSend(trimmed));
      // Only cleared on success, so a failed send never loses what was typed.
      setBody("");
      setPending([]);
      stopTyping();
    } catch (sendError) {
      setErrorKind("send");
      setError(
        sendError instanceof Error
          ? sendError.message
          : "Your message could not be sent.",
      );
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter breaks the line, which is what a chat composer
    // is expected to do.
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  const tooLong = body.length > MAX_MESSAGE_CHARS;
  const hasContent = Boolean(body.trim()) || readyIds.length > 0;
  const canSend =
    hasContent && !sending && !disabled && !tooLong && !uploading && !uploadFailed;

  return (
    <div className={cn("border-t bg-card p-3", className)}>
      {/* Upload progress, completion and failure. The bar alone is not spoken,
          and a file that fails would otherwise change silently. */}
      <p role="status" className="sr-only">
        {statusMessage}
      </p>
      {error ? (
        <div
          role="alert"
          className="mb-2 flex items-start gap-2 rounded-md border border-tone-danger-border bg-tone-danger-surface px-3 py-2 text-xs text-tone-danger-foreground"
        >
          <AlertCircle
            className="mt-0.5 h-3.5 w-3.5 shrink-0"
            aria-hidden="true"
          />
          <span className="flex-1">{error}</span>
          {errorKind === "send" ? (
            <button
              type="button"
              onClick={submit}
              className="font-medium underline underline-offset-2"
            >
              Retry
            </button>
          ) : null}
        </div>
      ) : null}

      {pending.length > 0 ? (
        <ul className="mb-2 flex flex-wrap gap-2" aria-label="Attachments">
          {pending.map((item) => (
            <li
              key={item.key}
              className={cn(
                "flex min-w-[min(10rem,100%)] max-w-[16rem] items-center gap-2 rounded-md border px-2 py-1.5 text-xs",
                item.status === "failed"
                  ? "border-tone-danger-border bg-tone-danger-surface"
                  : "bg-muted/40",
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{item.file.name}</p>
                {item.status === "uploading" ? (
                  <>
                    <div
                      className="mt-1 h-1 w-full overflow-hidden rounded-full bg-border"
                      role="progressbar"
                      aria-label={`Uploading ${item.file.name}`}
                      aria-valuenow={Math.round(item.progress * 100)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className="h-full bg-primary transition-[width]"
                        style={{ width: `${Math.round(item.progress * 100)}%` }}
                      />
                    </div>
                    <p className="mt-0.5 text-muted-foreground">
                      {Math.round(item.progress * 100)}%
                    </p>
                  </>
                ) : item.status === "failed" ? (
                  <p className="mt-0.5 text-tone-danger-foreground">
                    {item.error}{" "}
                    <button
                      type="button"
                      onClick={() => void startUpload(item)}
                      className="-my-2 inline-flex min-h-touch items-center font-medium underline underline-offset-2 md:my-0 md:min-h-0"
                    >
                      Retry upload
                    </button>
                  </p>
                ) : (
                  <p className="mt-0.5 text-muted-foreground">
                    {describeFile(item.file)} · {attachmentSizeLabel(item.file.size)}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => removePending(item.key)}
                aria-label={`Remove ${item.file.name}`}
                className="-my-2 -mr-2 flex min-h-touch min-w-touch shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted md:-my-1 md:-mr-1 md:min-h-0 md:min-w-0 md:p-1"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        {onUpload ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_ATTRIBUTE}
              className="hidden"
              data-testid="chat-attachment-input"
              onChange={(event) => {
                addFiles(event.target.files);
                // Reset so picking the same file twice still fires a change.
                event.target.value = "";
              }}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || pending.length >= MAX_ATTACHMENTS}
              aria-label="Attach a file"
              className="min-h-touch min-w-touch shrink-0"
            >
              <Paperclip className="h-4 w-4" aria-hidden="true" />
            </Button>
          </>
        ) : null}
        <Textarea
          value={body}
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          aria-label="Message"
          className="max-h-32 min-h-touch min-w-0 flex-1 basis-[10rem] resize-none text-base"
        />
        <Button
          type="button"
          size="icon"
          onClick={submit}
          disabled={!canSend}
          aria-label="Send message"
          className="min-h-touch min-w-touch shrink-0"
        >
          <SendHorizontal className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {body.length >= COUNTER_VISIBLE_FROM ? (
        <p
          className={cn(
            "mt-1 text-right text-xs",
            tooLong ? "text-tone-danger-foreground" : "text-muted-foreground",
          )}
        >
          {body.length} / {MAX_MESSAGE_CHARS}
        </p>
      ) : null}
    </div>
  );
}
